import { useState } from 'react'
import { Link, useNavigate, useLocation } from 'react-router-dom'
import { Button } from '../components/ui/button'
import { Crown, Eye, EyeOff, Mail, Lock } from 'lucide-react'
import { supabase } from '../services/supabase'

interface User {
  id: string
  name: string
  email: string
  level: number
  faction?: string
}

interface LoginPageProps {
  onLogin: (user: User) => void
}

const LoginPage = ({ onLogin }: LoginPageProps) => {
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [showPassword, setShowPassword] = useState(false)
  const [isLoading, setIsLoading] = useState(false)
  const [error, setError] = useState('')
  
  const navigate = useNavigate()
  const location = useLocation()
  
  const from = location.state?.from?.pathname || '/dashboard'

  const handleReportIssue = async () => {
    // Report issue to console/analytics (could be enhanced with actual reporting service)
    const issueReport = {
      timestamp: new Date().toISOString(),
      userAgent: navigator.userAgent,
      url: window.location.href,
      error: error || 'User reported login issue',
      email: email || 'not provided'
    }
    
    console.log('🚨 Login Issue Reported:', issueReport)
    
    // You could send this to an analytics service or logging endpoint
    // Example: sendToAnalytics('login_issue_reported', issueReport)
    
    alert('Thank you for reporting the issue! Our team will investigate. You can now skip to play the game.')
  }

  const handleSkipLogin = () => {
    // Create a guest user for gameplay
    const guestUser: User = {
      id: 'guest-' + Date.now(),
      name: 'Guest Commander',
      email: 'guest@crown-of-suns.local',
      level: 1,
      faction: 'Terran Empire'
    }
    
    console.log('🎮 Guest login activated')
    
    // Show notification about guest mode
    const notification = document.createElement('div')
    notification.className = 'fixed top-4 right-4 bg-green-600 text-white px-4 py-2 rounded-lg shadow-lg z-50'
    notification.innerHTML = '🎮 Playing as Guest - Progress won\'t be saved'
    document.body.appendChild(notification)
    
    setTimeout(() => {
      document.body.removeChild(notification)
    }, 3000)
    
    onLogin(guestUser)
    navigate('/dashboard', { replace: true }) // Use dashboard instead of game
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setError('')
    setIsLoading(true)

    try {
      // Authenticate with Supabase
      const { data, error } = await supabase.auth.signInWithPassword({
        email: email,
        password: password,
      })

      if (error) {
        console.error('Login error:', error)
        
        // Handle specific error cases
        if (error.message.includes('Invalid login credentials')) {
          setError('Invalid email or password. Please check your credentials.')
        } else if (error.message.includes('Email not confirmed')) {
          setError('Please check your email and click the confirmation link before signing in. Check your spam folder if needed.')
        } else if (error.message.includes('Too many requests')) {
          setError('Too many attempts. Please wait a moment before trying again.')
        } else {
          setError(`Login failed: ${error.message}`)
        }
        return
      }

      if (data.user) {
        // Get user profile from database
        const { data: profileData, error: profileError } = await supabase
          .from('user_profiles')
          .select('username, level, civilization_name')
          .eq('user_id', data.user.id)
          .single()
        
        let userName = 'Space Commander'
        let userLevel = 1
        let userFaction = 'Terran Empire'

        if (profileData && !profileError) {
          userName = profileData.username
          userLevel = profileData.level
          userFaction = profileData.civilization_name || 'Terran Empire'
        }

        const user: User = {
          id: data.user.id,
          name: userName,
          email: data.user.email || email,
          level: userLevel,
          faction: userFaction
        }
        
        onLogin(user)
        navigate(from, { replace: true })
      }
    } catch (err) {
      console.error('Unexpected login error:', err)
      setError('An unexpected error occurred. Please try again.')
    } finally {
      setIsLoading(false)
    }
  }

  return (
    <div className="min-h-screen bg-gradient-to-b from-slate-900 via-purple-900 to-slate-900 flex items-center justify-center px-4">
      <div className="w-full max-w-md">
        {/* Header */}
        <div className="text-center mb-8">
          <Crown className="w-16 h-16 text-purple-400 mx-auto mb-4" />
          <h1 className="text-3xl font-bold bg-gradient-to-r from-blue-400 to-purple-400 bg-clip-text text-transparent mb-2">
            Welcome Back
          </h1>
          <p className="text-gray-400">Sign in to continue your cosmic conquest</p>
        </div>

        {/* Login Form */}
        <div className="bg-slate-800/50 backdrop-blur-sm rounded-lg p-6 border border-white/10">
          <form onSubmit={handleSubmit} className="space-y-6">
            {error && (
              <div className="bg-red-500/10 border border-red-500/30 text-red-400 px-4 py-3 rounded-lg text-sm">
                {error}
              </div>
            )}

            {/* Email Field */}
            <div>
              <label htmlFor="email" className="block text-sm font-medium text-gray-300 mb-2">
                Email Address
              </label>
              <div className="relative">
                <Mail className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
                <input
                  id="email"
                  name="email"
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="w-full bg-slate-700/50 border border-gray-600 rounded-lg pl-10 pr-4 py-3 text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                  placeholder="commander@empire.space"
                  autoComplete="email"
                  required
                />
              </div>
            </div>

            {/* Password Field */}
            <div>
              <label htmlFor="password" className="block text-sm font-medium text-gray-300 mb-2">
                Password
              </label>
              <div className="relative">
                <Lock className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
                <input
                  id="password"
                  name="password"
                  type={showPassword ? 'text' : 'password'}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="w-full bg-slate-700/50 border border-gray-600 rounded-lg pl-10 pr-12 py-3 text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                  placeholder="Enter your password"
                  autoComplete="current-password"
                  required
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-white"
                >
                  {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                </button>
              </div>
            </div>

            {/* Remember Me & Forgot Password */}
            <div className="flex items-center justify-between">
              <label className="flex items-center">
                <input
                  type="checkbox"
                  className="w-4 h-4 text-purple-600 bg-slate-700 border-gray-600 rounded focus:ring-purple-500 focus:ring-2"
                />
                <span className="ml-2 text-sm text-gray-300">Remember me</span>
              </label>
              <Link to="/forgot-password" className="text-sm text-purple-400 hover:text-purple-300">
                Forgot password?
              </Link>
            </div>

            {/* Submit Button */}
            <Button
              type="submit"
              disabled={isLoading}
              className="w-full bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-700 hover:to-purple-700 py-3"
            >
              {isLoading ? 'Signing In...' : 'Sign In'}
            </Button>
          </form>

          {/* Sign Up Link */}
          <div className="mt-6 text-center">
            <p className="text-gray-400">
              Don't have an account?{' '}
              <Link to="/register" className="text-purple-400 hover:text-purple-300 font-medium">
                Create your empire
              </Link>
            </p>
          </div>
        </div>

        {/* Issue Reporting & Skip Login Section */}
        <div className="mt-6 bg-slate-800/30 backdrop-blur-sm rounded-lg p-4 border border-yellow-500/20">
          <div className="text-center">
            <h3 className="text-yellow-400 font-semibold mb-2">Having Login Issues?</h3>
            <p className="text-gray-400 text-sm mb-4">
              Report the problem and skip to play the game while we fix it!
            </p>
            
            <div className="space-y-3">
              <Button
                onClick={handleReportIssue}
                className="w-full bg-yellow-600 hover:bg-yellow-700 text-white py-2"
                variant="outline"
              >
                📝 Report Login Issue
              </Button>
              
              <Button
                onClick={handleSkipLogin}
                className="w-full bg-green-600 hover:bg-green-700 text-white py-2"
              >
                🚀 Skip Login & Play Game
              </Button>
            </div>
            
            <p className="text-xs text-gray-500 mt-2">
              Game progress won't be saved in guest mode
            </p>
          </div>
        </div>

        {/* Back to Home */}
        <div className="mt-6 text-center">
          <Link to="/" className="text-gray-400 hover:text-white transition-colors">
            ← Back to Home
          </Link>
        </div>
      </div>
    </div>
  )
}

export default LoginPage